import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { INITIAL_PLAN_TERM_RATES } from "./costs.constants";
import { calculateProcedureCosts } from "./costs.utils";

import { accountApi } from "Care/features/account/account.api";
import type { GetCoveragesResponse } from "Care/features/account/account.types";
import type { Coverage, ProcedureQuote } from "Care/features/quote/quote.types";

import type { PlanSimulatorForm } from "./costs.forms";
import type { CalculatedCosts } from "./costs.types";

export interface CostsState {
  remaining_deductible: number;
  remaining_oop_max: number;
  selectedPlanTerm: string;
  byPlanTerm: Record<string, CostNode>;
  byProcedure: Record<ProcedureQuote["id"], CostNode>;
}

export interface CoverageUpdatePayload {
  remaining_deductible: number;
  remaining_oop_max: number;
}

export interface CostNode {
  rate: number;
  coverage: Coverage;
  expenses: CalculatedCosts;
}

// Initial state
const initialState: CostsState = {
  remaining_deductible: 0,
  remaining_oop_max: 0,
  selectedPlanTerm: undefined,
  byPlanTerm: {},
  byProcedure: {},
};

// Slice
export const costsSlice = createSlice({
  name: "costs",
  initialState,
  reducers: {
    updatePlanTermRates(state, action: PayloadAction<PlanSimulatorForm>) {
      const { spent_deductible: remaining_deductible, spent_oop: remaining_oop_max, rates } = action.payload;
      state.remaining_deductible = remaining_deductible;
      state.remaining_oop_max = remaining_oop_max;
      Object.entries(rates).forEach(([term]) => {
        if (state.byPlanTerm[term]) {
          state.byPlanTerm[term].rate = rates[term];
          state.byPlanTerm[term].expenses = calculateProcedureCosts(rates[term], state.byPlanTerm[term].coverage, {
            remaining_deductible: state.remaining_deductible,
            remaining_oop_max: state.remaining_oop_max,
          });
        }
      });
    },
    selectTerm(state, action: PayloadAction<string>) {
      const nextTerm = action.payload;
      state.selectedPlanTerm = state.selectedPlanTerm === nextTerm ? undefined : nextTerm;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(accountApi.endpoints.getCoverages.matchFulfilled, (state, action: PayloadAction<GetCoveragesResponse>) => {
      Object.entries(action.payload).forEach(([term, coverage]) => {
        const rate = INITIAL_PLAN_TERM_RATES[term] || 0;
        state.byPlanTerm[term] = {
          rate,
          coverage,
          expenses: calculateProcedureCosts(rate, coverage, {
            remaining_deductible: state.remaining_deductible,
            remaining_oop_max: state.remaining_oop_max,
          }),
        };
      });
    });
  },
});

export const { updatePlanTermRates, selectTerm } = costsSlice.actions;
